// SetupAccount.jsx
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from './config.js';
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import {
  LockReset as LockResetIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const SetupAccount = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [strength, setStrength] = useState({ level: "weak", color: "error", message: "Too weak" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Password strength checker
  useEffect(() => {
    if (password.length === 0) {
      setStrength({ level: "weak", color: "error", message: "Enter a password" });
    } else if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*]/.test(password)) {
      setStrength({ level: "strong", color: "success", message: "Strong password" });
    } else if (password.length >= 6 && (/[A-Z]/.test(password) || /[0-9]/.test(password))) {
      setStrength({ level: "medium", color: "warning", message: "Medium strength" });
    } else {
      setStrength({ level: "weak", color: "error", message: "Weak password" });
    }
  }, [password]);

  const getStrengthProgress = () => {
    switch (strength.level) {
      case "strong": return 100;
      case "medium": return 66;
      case "weak": return 33;
      default: return 0;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/setup-account/`, {
        uidb64,
        token,
        new_password: password
      });
      setSuccess("Account activated successfully! Redirecting to login...");
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <Box
      sx={{
        height:"100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f4f6f8",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <LockResetIcon
              sx={{
                fontSize: 64,
                color: "primary.main",
                backgroundColor: "primary.light",
                p: 1.5,
                borderRadius: "50%",
                opacity: 0.9,
              }}
            />
          </Box>

          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Setup Your Account
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Create a password to activate your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              variant="outlined"
              placeholder="Enter new password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password strength indicator */}
            {password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: "100%",
                    height: 4,
                    backgroundColor: "grey.200",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${getStrengthProgress()}%`,
                      height: "100%",
                      backgroundColor: `${strength.color}.main`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color={`${strength.color}.main`}
                  sx={{ mt: 0.5, display: "block", textAlign: "left" }}
                >
                  {strength.message}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              margin="normal"
              variant="outlined"
              placeholder="Confirm password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={confirmPassword && password !== confirmPassword}
              helperText={
                confirmPassword && password !== confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />

            {/* Password requirements */}
            <Box sx={{ mt: 2, mb: 3, textAlign: "left" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Password must contain:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
                <Typography
                  component="li"
                  variant="caption"
                  color={password.length >= 6 ? "success.main" : "text.secondary"}
                >
                  At least 6 characters
                </Typography>
                <Typography
                  component="li"
                  variant="caption"
                  color={/[A-Z]/.test(password) ? "success.main" : "text.secondary"}
                >
                  At least one uppercase letter
                </Typography>
                <Typography
                  component="li"
                  variant="caption"
                  color={/[0-9]/.test(password) ? "success.main" : "text.secondary"}
                >
                  At least one number
                </Typography>
                <Typography
                  component="li"
                  variant="caption"
                  color={/[!@#$%^&*]/.test(password) ? "success.main" : "text.secondary"}
                >
                  At least one special character (!@#$%^&*)
                </Typography>
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 2, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Activate Account"
              )}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              If you did not request this, please ignore this page.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Link
                href="/"
                variant="body2"
                sx={{ cursor: "pointer", textDecoration: "none" }}
              >
                Already have an account? Sign in
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SetupAccount;